import { useEffect, useRef, useState } from "react";
import { DENSE_ROW_PAGE_SIZE } from "@wmf/shared/pagination";
import type { EngagementAccount, NoteDomain, NoteEngagement, NoteViewEvent, NoteViewHistoryPage, NoteViewSummary, StudentEngagement, WorkFeedback } from "@wmf/shared/studentEngagement";
import { get, send } from "../api.js";
import { formatDate, formatDuration, formatTime } from "../format.js";
import { useAsync, type Async } from "../useAsync.js";
import "./engagement.css";

export function activityTime(value: string | null): string {
  return value ? `${formatDate(value)} at ${formatTime(value)}` : "No recorded view";
}

function AccountActivity({ accounts }: { accounts: EngagementAccount[] }) {
  return accounts.length ? <ul className="engagement-accounts">
    {accounts.map((account, index) => <li key={`${account.relationship}:${index}`}>
      <strong>{account.label}</strong>
      <span className="small">Last login: {account.lastLoginAt ? activityTime(account.lastLoginAt) : "Never logged in"}</span>
    </li>)}
  </ul> : <p className="small muted">No Hub account linked.</p>;
}

export function NoteActivitySummary({ activity }: { activity: NoteViewSummary }) {
  return <div className="engagement-note-summary small">
    <span>Student: {activityTime(activity.studentLastViewedAt)}</span>
    <span>Parent: {activityTime(activity.parentLastViewedAt)}</span>
    {activity.lastViewedAt && !activity.currentVersionViewed && <span className="muted">Latest edit has no recorded view.</span>}
  </div>;
}

/** A successful scoped response may omit a colleague's note; that is not a failed read. */
export function NoteActivityStatus({ activity, loading, error, loaded }: {
  activity?: NoteViewSummary; loading: boolean; error: string | null; loaded: boolean;
}) {
  if (error) return <p className="small muted">Note activity could not be loaded.</p>;
  if (activity) return <NoteActivitySummary activity={activity} />;
  if (loading || !loaded) return <p className="small muted">Loading note activity…</p>;
  return null;
}

export function StudentActivityPanel({ domain = "private", id }: { domain?: NoteDomain; id: string }) {
  const path = domain === "private" ? `/engagement/students/${id}` : `/engagement/school/pupils/${id}`;
  return <StudentActivityContent key={path} path={path} />;
}
function StudentActivityContent({ path }: { path: string }) {
  const { data, error, loading } = useAsync(() => get<StudentEngagement>(path), [path]);
  return <StudentActivitySummary data={data} error={error} loading={loading} />;
}
export function StudentActivitySummary({ data, error, loading }: {
  data: StudentEngagement | null; error: string | null; loading: boolean;
}) {
  return <section className="panel engagement-panel" aria-label="Hub activity">
    <h2>Hub activity</h2>
    {loading && !data && <p className="small muted">Loading activity…</p>}
    {error ? <p className="small muted">Activity could not be loaded.</p> : data && <>
      <AccountActivity accounts={data.accounts} />
      <p><strong>{data.viewedNotes} of {data.publishedNotes}</strong> published notes have a recorded student or parent view.</p>
      <p className="small muted">Parent login times apply to their account. Note views relate to this student.</p>
      <p className="small muted">{data.trackingStartedAt ? `Note-view tracking started ${activityTime(data.trackingStartedAt)}.` : "No note views recorded yet."} Opening a note does not confirm reading or practice.</p>
    </>}
  </section>;
}

export function NoteActivityPanel({ domain, noteId, showAccounts = false, revision, source, showFeedback = true }: { domain: NoteDomain; noteId: string; showAccounts?: boolean; revision?: string | null; source?: Async<NoteEngagement>; showFeedback?: boolean }) {
  const path = `/engagement/notes/${domain}/${noteId}`;
  return source
    ? <NoteActivityContent key={path} path={path} showAccounts={showAccounts} source={source} showFeedback={showFeedback} />
    : <NoteActivityLoader key={path} path={path} showAccounts={showAccounts} revision={revision} showFeedback={showFeedback} />;
}
function NoteActivityLoader({ path, showAccounts, revision, showFeedback }: { path: string; showAccounts: boolean; revision?: string | null; showFeedback: boolean }) {
  const source = useAsync(() => get<NoteEngagement>(path + "?historyPageSize=" + DENSE_ROW_PAGE_SIZE), [path, revision]);
  return <NoteActivityContent path={path} showAccounts={showAccounts} source={source} showFeedback={showFeedback} />;
}
function NoteActivityContent({ path, showAccounts, source, showFeedback }: { path: string; showAccounts: boolean; source: Async<NoteEngagement>; showFeedback: boolean }) {
  const { data, error, loading, reload } = source;
  const requestGeneration = useRef(0);
  const [history, setHistory] = useState(() => ({
    views: data?.views ?? [] as NoteViewEvent[],
    hasMore: data?.historyHasMore === true,
    cursor: data?.historyNextCursor ?? null,
    loading: false,
    error: null as string | null,
  }));
  useEffect(() => {
    requestGeneration.current += 1;
    setHistory({ views: data?.views ?? [], hasMore: data?.historyHasMore === true,
      cursor: data?.historyNextCursor ?? null, loading: false, error: null });
    return () => { requestGeneration.current += 1; };
  }, [path, data]);

  async function loadOlderViews() {
    const cursor = history.cursor;
    if (!cursor || history.loading) return;
    const generation = ++requestGeneration.current;
    setHistory((current) => ({ ...current, loading: true, error: null }));
    try {
      const page = await get<NoteViewHistoryPage>(`${path}/history?cursor=${encodeURIComponent(cursor)}`);
      if (generation !== requestGeneration.current) return;
      setHistory((current) => {
        const known = new Set(current.views.flatMap((view) => view.id ? [view.id] : []));
        const additions: NoteViewEvent[] = [];
        for (const view of page.views) {
          if (known.has(view.id)) continue;
          known.add(view.id);
          additions.push(view);
        }
        return { views: [...current.views, ...additions], hasMore: page.historyHasMore,
          cursor: page.historyNextCursor, loading: false, error: null };
      });
    } catch {
      if (generation !== requestGeneration.current) return;
      setHistory((current) => ({ ...current, loading: false, error: "Older views could not be loaded." }));
    }
  }

  return <section className="panel engagement-panel" aria-label="Lesson note activity">
    <h2>Note activity</h2>
    {loading && !data && <p className="small muted">Loading activity…</p>}
    {error ? <p className="small muted">Note activity could not be loaded.</p> : data && <>
      {data.publishedAt === null ? <p className="small muted">Publish this note to make it available to the family.</p> : <>
        <NoteActivitySummary activity={data} />
        {data.viewCount > 0 && <details className="engagement-history">
          <summary>{data.viewCount} {data.viewCount === 1 ? "view" : "views"} · viewing history</summary>
          <p className="small muted">First viewed: {activityTime(data.firstViewedAt)}</p>
          <p className="small muted" role="status" aria-live="polite">{data.historyNextCursor === undefined && data.historyHasMore
            ? "Showing the latest 50 views."
            : `Showing ${history.views.length} of ${data.viewCount} recorded ${data.viewCount === 1 ? "view" : "views"}.`}</p>
          <ol>{history.views.map((view, index) => <li key={view.id ?? `${view.viewedAt}:${view.relationship}:${index}`}><span>{view.label}</span> <time dateTime={view.viewedAt}>{activityTime(view.viewedAt)}</time></li>)}</ol>
          {history.error && <p className="small muted" role="alert">{history.error} The views already shown are still available.</p>}
          {history.hasMore && history.cursor && <button type="button" className="action-tertiary small" onClick={() => void loadOlderViews()} disabled={history.loading}>
            {history.loading ? "Loading older views…" : history.error ? "Retry older views" : "Load older views"}
          </button>}
        </details>}
      </>}
      {showAccounts && <AccountActivity accounts={data.accounts} />}
      <p className="small muted">{data.trackingStartedAt ? `Tracking started ${activityTime(data.trackingStartedAt)}.` : "No note views recorded yet."}</p>
      {data.work && <details className="engagement-history">
        <summary>Work since this lesson</summary>
        <p className="small muted">{activityTime(data.work.from)} to {activityTime(data.work.to)}{data.work.nextLessonAt && data.work.to === data.work.nextLessonAt ? " · next lesson" : ""}.</p>
        {data.work.linkedTasks === 0 ? <p>No practice tasks linked to this lesson, so work totals are unavailable.</p> : <>
          <p>{data.work.practiceSeconds === 0 ? "No attributable practice logged." : `${formatDuration(data.work.practiceSeconds)} logged over ${data.work.practiceDays} ${data.work.practiceDays === 1 ? "day" : "days"}.`}</p>
          <p>{data.work.completedTasks} of {data.work.linkedTasks} linked tasks completed.</p>
        </>}
        <p className="small muted">Includes practice explicitly linked to this note’s tasks. Other practice and work done offline remain outside these totals.</p>
        {data.work.autoStoppedSessions > 0 && <p className="small muted">{data.work.autoStoppedSessions} practice sessions were stopped automatically; their duration is an estimate.</p>}
      </details>}
      {data.work === null && <p className="small muted">Work totals need a completed, dated lesson and a linked learner record.</p>}
      {showFeedback && <WorkFeedbackEditor path={path} feedback={data.feedback} onSaved={reload} />}
    </>}
  </section>;
}

const RATING_LABELS = ["1 — Little evidence", "2 — Some work", "3 — Steady work", "4 — Strong work", "5 — Excellent work"];
function WorkFeedbackEditor({ path, feedback, onSaved }: { path: string; feedback: WorkFeedback | null; onSaved: () => void }) {
  const [effort, setEffort] = useState("");
  const [progress, setProgress] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    setEffort(feedback?.effortRating?.toString() ?? "");
    setProgress(feedback?.progressRating?.toString() ?? "");
    setComment(feedback?.comment ?? "");
  }, [feedback?.version, path]);
  async function save() {
    setBusy(true); setProblem(null); setSaved(false);
    try {
      await send("PUT", `${path}/feedback`, { effortRating: effort ? Number(effort) : null,
        progressRating: progress ? Number(progress) : null, comment, version: feedback?.version ?? 0 });
      setSaved(true); onSaved();
    } catch (error) { setProblem(error instanceof Error ? error.message : "Feedback could not be saved."); }
    finally { setBusy(false); }
  }
  return <details className="engagement-feedback">
    <summary>Work since this lesson · tutor feedback</summary>
    <p className="small muted">Assess the work since this lesson when you have evidence. Feedback is staff-only and prepares inputs for a future score.</p>
    <div role="group" aria-label="Tutor work feedback">
      <div className="engagement-feedback-fields">
        <label>Effort<select disabled={busy} value={effort} onChange={(e) => { setEffort(e.target.value); setSaved(false); }}>
          <option value="">Not assessed</option>{RATING_LABELS.map((label, i) => <option key={i} value={i + 1}>{label}</option>)}
        </select></label>
        <label>Progress<select disabled={busy} value={progress} onChange={(e) => { setProgress(e.target.value); setSaved(false); }}>
          <option value="">Not assessed</option>{["1 — Little demonstrated progress", "2 — Early progress", "3 — Steady progress", "4 — Strong progress", "5 — Excellent progress"].map((label, i) => <option key={i} value={i + 1}>{label}</option>)}
        </select></label>
        <label>Feedback<textarea disabled={busy} value={comment} maxLength={4000} rows={3} onChange={(e) => { setComment(e.target.value); setSaved(false); }} /></label>
        <button type="button" onClick={() => void save()} disabled={busy}>{busy ? "Saving…" : "Save feedback"}</button>
        {saved && <p className="small" role="status">Feedback saved.</p>}
        {problem && <><p className="error small" role="alert">{problem}</p><button type="button" className="quiet" onClick={onSaved} disabled={busy}>Reload feedback</button></>}
      </div>
    </div>
  </details>;
}
